import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Power, PowerOff, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Boxes, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto } from '../utils/format';
import { limpiarRif } from '../utils/rif';
import EstadoCuenta from '../components/EstadoCuenta';
import AutocompleteGrupo from '../components/AutocompleteGrupo';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

const FORM_VACIO = { nombre: '', rif: '', telefono: '', email: '' };

export default function Clientes() {
  const { empresaActiva, empresas } = useAuth();
  const empresaData = empresas.find((e) => String(e.empresa_id) === String(empresaActiva));
  const empresaNombre = empresaData?.empresa_nombre;
  const esRestaurante = empresaData?.rubro === 'restaurante';

  const [clientes, setClientes] = useState([]);
  const [clientesTodas, setClientesTodas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('');

  // Al volver de la pantalla de carga de factura se reabre el mismo cliente.
  // Desde el Inicio llega además `accion: 'pago'` (Registrar cobranza): se
  // abre una sola vez y se borra del historial para que no se repita al recargar.
  const { state: navState, pathname } = useLocation();
  const navigate = useNavigate();
  const [seleccionadoId, setSeleccionadoId] = useState(navState?.clienteId ?? null);
  const [accionInicial, setAccionInicial] = useState(navState?.accion ?? null);
  // Desde el buscador general se puede llegar estando ya en Clientes.
  useEffect(() => {
    if (navState?.clienteId) setSeleccionadoId(navState.clienteId);
  }, [navState?.clienteId]);
  useEffect(() => {
    if (navState?.accion) navigate(pathname, { replace: true, state: { clienteId: navState.clienteId } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [listaColapsada, setListaColapsada] = useState(false);
  const [datosColapsados, setDatosColapsados] = useState(true);
  const clienteSeleccionado = clientes.find((c) => c.id === seleccionadoId);

  // --- Agrupación (mismo patrón que Productos) ---
  const [gruposExpandidos, setGruposExpandidos] = useState(new Set());
  const [grupoAbiertoId, setGrupoAbiertoId] = useState(null);     // panel "agrupar cliente"
  const [errorGrupo, setErrorGrupo] = useState('');
  const [grupoPadrePanelId, setGrupoPadrePanelId] = useState(null); // panel "meter grupo dentro de otro"
  const [errorGrupoPadre, setErrorGrupoPadre] = useState('');
  const [grupoEditId, setGrupoEditId] = useState(null);
  const [grupoEditNombre, setGrupoEditNombre] = useState('');
  const [errorGrupoEdit, setErrorGrupoEdit] = useState('');

  async function cargar() {
    const [cli, cliTodas, grup] = await Promise.all([
      api.get('/clientes'),
      api.get('/clientes/todas'),
      api.get('/clientes-grupo')
    ]);
    setClientes(cli.data);
    setClientesTodas(cliTodas.data);
    setGrupos(grup.data);
  }

  useEffect(() => {
    if (empresaActiva) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  useEffect(() => {
    setDatosColapsados(true);
  }, [seleccionadoId]);

  function empezarEdicion(c) {
    setEditandoId(c.id);
    setForm({
      nombre: c.nombre,
      rif: c.rif || '',
      telefono: c.telefono || '',
      email: c.email || ''
    });
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setEditandoId(null);
    setForm(FORM_VACIO);
    setError('');
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    try {
      if (editandoId) {
        await api.put(`/clientes/${editandoId}`, form);
      } else {
        await api.post('/clientes', form);
      }
      cancelar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el cliente');
    }
  }

  async function toggleActivo(cliente) {
    const accion = cliente.activo ? 'Inhabilitar' : 'Habilitar';
    if (!window.confirm(`¿${accion} al cliente "${cliente.nombre}"?`)) return;
    try {
      await api.put(`/clientes/${cliente.id}`, { activo: cliente.activo ? 0 : 1 });
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || `No se pudo ${accion.toLowerCase()} el cliente`);
    }
  }

  // Clientes de otras empresas (para no importar uno que ya es de esta empresa activa).
  const clientesImportables = clientesTodas.filter((c) => String(c.empresa_id) !== String(empresaActiva));

  function importarDatos(clienteOrigenId) {
    if (!clienteOrigenId) return;
    const origen = clientesTodas.find((c) => String(c.id) === String(clienteOrigenId));
    if (!origen) return;
    setForm({ nombre: origen.nombre, rif: origen.rif || '', telefono: origen.telefono || '', email: origen.email || '' });
  }

  // ---------- Operaciones de grupo ----------
  function alternarExpandirGrupo(id) {
    setGruposExpandidos((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  function alternarGrupoPanel(id) {
    setGrupoAbiertoId((prev) => (prev === id ? null : id));
    setErrorGrupo('');
  }
  async function asignarGrupo(cliente, grupoId) {
    setErrorGrupo('');
    try {
      await api.put(`/clientes/${cliente.id}`, { grupo_id: grupoId });
      setGruposExpandidos((prev) => new Set(prev).add(grupoId));
      setGrupoAbiertoId(null);
      cargar();
    } catch (err) {
      setErrorGrupo(err.response?.data?.error || 'No se pudo asignar el grupo');
    }
  }
  async function crearYAsignarGrupo(cliente, nombre) {
    setErrorGrupo('');
    try {
      const { data } = await api.post('/clientes-grupo', { nombre });
      await asignarGrupo(cliente, data.id);
    } catch (err) {
      setErrorGrupo(err.response?.data?.error || 'No se pudo crear el grupo');
    }
  }
  async function quitarDeGrupo(cliente) {
    setErrorGrupo('');
    try {
      await api.put(`/clientes/${cliente.id}`, { grupo_id: null });
      setGrupoAbiertoId(null);
      cargar();
    } catch (err) {
      setErrorGrupo(err.response?.data?.error || 'No se pudo quitar del grupo');
    }
  }

  function alternarPadrePanel(id, e) {
    e?.stopPropagation();
    setGrupoPadrePanelId((prev) => (prev === id ? null : id));
    setErrorGrupoPadre('');
  }
  async function asignarPadre(grupoId, padreId) {
    setErrorGrupoPadre('');
    try {
      await api.put(`/clientes-grupo/${grupoId}`, { padre_id: padreId });
      setGruposExpandidos((prev) => new Set(prev).add(padreId).add(grupoId));
      setGrupoPadrePanelId(null);
      cargar();
    } catch (err) {
      setErrorGrupoPadre(err.response?.data?.error || 'No se pudo mover el grupo');
    }
  }
  async function crearYAsignarPadre(grupoId, nombre) {
    setErrorGrupoPadre('');
    try {
      const { data } = await api.post('/clientes-grupo', { nombre });
      await asignarPadre(grupoId, data.id);
    } catch (err) {
      setErrorGrupoPadre(err.response?.data?.error || 'No se pudo crear el grupo');
    }
  }
  async function quitarPadre(grupoId) {
    setErrorGrupoPadre('');
    try {
      await api.put(`/clientes-grupo/${grupoId}`, { padre_id: null });
      setGrupoPadrePanelId(null);
      cargar();
    } catch (err) {
      setErrorGrupoPadre(err.response?.data?.error || 'No se pudo sacar del grupo');
    }
  }

  function abrirEditGrupo(g, e) {
    e?.stopPropagation();
    setGrupoEditId((prev) => (prev === g.id ? null : g.id));
    setGrupoEditNombre(g.nombre);
    setErrorGrupoEdit('');
  }
  async function guardarEditGrupo(id) {
    setErrorGrupoEdit('');
    try {
      await api.put(`/clientes-grupo/${id}`, { nombre: grupoEditNombre });
      setGrupoEditId(null);
      cargar();
    } catch (err) {
      setErrorGrupoEdit(err.response?.data?.error || 'No se pudo guardar');
    }
  }
  async function eliminarGrupo(g, e) {
    e?.stopPropagation();
    if (!window.confirm(`¿Eliminar el grupo "${g.nombre}"? Sus clientes y subgrupos quedan sueltos, sin agrupar.`)) return;
    try {
      await api.delete(`/clientes-grupo/${g.id}`);
      setGrupoEditId(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el grupo');
    }
  }

  // ---------- Filtro + árbol de grupos ----------
  const q = filtro.trim().toLowerCase();
  const gruposPorId = new Map(grupos.map((g) => [g.id, g]));

  // Un grupo "coincide" si su nombre —o el de cualquiera de sus
  // ancestros— contiene el texto buscado; entonces todos sus clientes
  // se mantienen visibles.
  const grupoIdsMatch = new Set();
  if (q) {
    grupos.forEach((g) => {
      let cur = g;
      const vistos = new Set();
      while (cur && !vistos.has(cur.id)) {
        vistos.add(cur.id);
        if (cur.nombre.toLowerCase().includes(q)) { grupoIdsMatch.add(g.id); break; }
        cur = cur.padre_id ? gruposPorId.get(cur.padre_id) : null;
      }
    });
  }

  const coincideCliente = (c) => {
    if (!q) return true;
    const texto = [c.nombre, c.telefono, c.email].some((v) => v?.toLowerCase().includes(q));
    const rif = limpiarRif(c.rif).includes(limpiarRif(filtro));
    if (texto || rif) return true;
    return !!(c.grupo_id && grupoIdsMatch.has(c.grupo_id));
  };
  const clientesVisibles = clientes.filter(coincideCliente);

  // Árbol de profundidad libre. Nodo de grupo:
  //   { esGrupo:true, grupo_id, grupo_nombre, padre_id, miembros:[], hijosVisibles:[], visible, saldo, conteo }
  // Clientes sueltos: { esGrupo:false, cliente }.
  const entradasTop = (() => {
    const miembrosPorGrupo = new Map();
    const sueltos = [];
    clientesVisibles.forEach((c) => {
      if (c.grupo_id && gruposPorId.has(c.grupo_id)) {
        if (!miembrosPorGrupo.has(c.grupo_id)) miembrosPorGrupo.set(c.grupo_id, []);
        miembrosPorGrupo.get(c.grupo_id).push(c);
      } else {
        sueltos.push({ esGrupo: false, cliente: c });
      }
    });
    miembrosPorGrupo.forEach((arr) => arr.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));

    const nodoPorId = new Map();
    grupos.forEach((g) => {
      nodoPorId.set(g.id, {
        esGrupo: true,
        grupo_id: g.id,
        grupo_nombre: g.nombre,
        padre_id: g.padre_id || null,
        padre_nombre: g.padre_nombre || null,
        miembros: miembrosPorGrupo.get(g.id) || [],
        hijos: [],
        hijosVisibles: [],
        visible: false,
        saldo: 0,
        conteo: 0
      });
    });

    const raices = [];
    nodoPorId.forEach((nodo) => {
      const padre = nodo.padre_id ? nodoPorId.get(nodo.padre_id) : null;
      if (padre) padre.hijos.push(nodo);
      else raices.push(nodo);
    });

    const resolver = (nodo) => {
      nodo.hijos.forEach(resolver);
      nodo.hijos.sort((a, b) => a.grupo_nombre.localeCompare(b.grupo_nombre, 'es'));
      nodo.hijosVisibles = nodo.hijos.filter((h) => h.visible);
      nodo.visible = nodo.miembros.length > 0 || nodo.hijosVisibles.length > 0;
      nodo.saldo = nodo.miembros.reduce((s, c) => s + Number(c.pendiente_cobrar_usd || 0), 0)
        + nodo.hijosVisibles.reduce((s, h) => s + h.saldo, 0);
      nodo.conteo = nodo.miembros.length + nodo.hijosVisibles.reduce((s, h) => s + h.conteo, 0);
    };
    raices.forEach(resolver);

    return [...raices.filter((n) => n.visible), ...sueltos].sort((a, b) => {
      const ka = a.esGrupo ? a.grupo_nombre : a.cliente.nombre;
      const kb = b.esGrupo ? b.grupo_nombre : b.cliente.nombre;
      return ka.localeCompare(kb, 'es');
    });
  })();

  const candidatosPadre = (grupoId) => {
    const desc = new Set();
    (function marcar(id) {
      grupos.forEach((g) => {
        if (g.padre_id === id && !desc.has(g.id)) { desc.add(g.id); marcar(g.id); }
      });
    })(grupoId);
    return grupos
      .filter((g) => g.id !== grupoId && !desc.has(g.id))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Clientes
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        <button
          onClick={() => setMostrarForm(true)}
          title="Nuevo cliente"
          className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md self-start sm:self-auto"
        >
          <Plus size={18} />
        </button>
      </div>

      {mostrarForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="font-medium text-slate-600">
                {editandoId ? 'Editando cliente' : 'Nuevo cliente'}
              </p>
              <button onClick={cancelar} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {!editandoId && clientesImportables.length > 0 && (
                <div className="sm:col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Importar datos de un cliente de otra empresa (opcional)</label>
                  <select
                    defaultValue=""
                    onChange={(e) => importarDatos(e.target.value)}
                    className="border rounded-md px-3 py-2 w-full"
                  >
                    <option value="">Escribir a mano...</option>
                    {clientesImportables.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre} ({c.empresa_nombre})</option>
                    ))}
                  </select>
                </div>
              )}

              <input
                required
                placeholder="Nombre del cliente"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="border rounded-md px-3 py-2 sm:col-span-2"
              />
              <input
                placeholder="RIF (opcional)"
                value={form.rif}
                onChange={(e) => setForm({ ...form, rif: e.target.value })}
                className="border rounded-md px-3 py-2"
              />
              <input
                placeholder="Teléfono (opcional)"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="border rounded-md px-3 py-2"
              />
              <input
                type="email"
                placeholder="Email (opcional)"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="border rounded-md px-3 py-2 sm:col-span-2"
              />
              <p className="text-xs text-slate-400 sm:col-span-2">
                El saldo pendiente se calcula automáticamente a partir de las facturas y sus pagos.
                {!editandoId && ' El grupo se asigna desde la lista, con el ícono de agrupar.'}
              </p>
              {error && <p className="text-red-600 text-sm sm:col-span-2">{error}</p>}
              <button type="submit" className="bg-primary-600 text-white rounded-md py-2 sm:col-span-2 hover:bg-primary-700">
                {editandoId ? 'Guardar cambios' : 'Guardar'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className={`bg-white rounded-xl shadow-sm overflow-hidden shrink-0 transition-all ${listaColapsada ? 'w-11' : seleccionadoId ? 'w-full lg:max-w-[30%]' : 'w-full lg:max-w-[70%]'}`}>
          <div className="flex items-center justify-between p-2 border-b">
            {!listaColapsada && <span className="text-xs font-medium text-slate-500 px-1">Clientes</span>}
            <button
              onClick={() => setListaColapsada(!listaColapsada)}
              title={listaColapsada ? 'Expandir lista' : 'Minimizar lista'}
              className="p-1 rounded-md text-slate-400 hover:text-primary-600 hover:bg-slate-50 transition-colors ml-auto"
            >
              {listaColapsada ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
          {!listaColapsada && (
            <div className="p-2 border-b">
              <input
                placeholder="Buscar por nombre, RIF, teléfono, email o grupo..."
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                className="border rounded-md px-3 py-2 w-full text-sm"
              />
            </div>
          )}
          {!listaColapsada && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-3">Nombre</th>
                  <th className="p-3 text-right w-24">Saldo</th>
                  <th className="p-3 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {entradasTop.map((entrada) =>
                  entrada.esGrupo ? renderGrupo(entrada, 0) : renderFilaCliente(entrada.cliente, 0)
                )}
                {entradasTop.length === 0 && (
                  <tr>
                    <td colSpan={3} className="p-6 text-center text-slate-400">
                      {clientes.length === 0 ? 'No hay clientes creados todavía.' : 'Ningún cliente coincide con el filtro.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {seleccionadoId && clienteSeleccionado && (
        <div className="w-full lg:flex-1 bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-medium text-slate-700">{clienteSeleccionado.nombre}</h2>
                  {clienteSeleccionado.grupo_nombre && (
                    <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 whitespace-nowrap">
                      {clienteSeleccionado.grupo_nombre}
                    </span>
                  )}
                  <span
                    className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                      clienteSeleccionado.activo ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {clienteSeleccionado.activo ? 'Activo' : 'Inhabilitado'}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    onClick={() => empezarEdicion(clienteSeleccionado)}
                    title="Editar"
                    className="text-slate-400 hover:text-primary-600 transition-colors"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => toggleActivo(clienteSeleccionado)}
                    title={clienteSeleccionado.activo ? 'Inhabilitar' : 'Habilitar'}
                    className={`transition-colors ${clienteSeleccionado.activo ? 'text-slate-400 hover:text-red-600' : 'text-slate-400 hover:text-green-600'}`}
                  >
                    {clienteSeleccionado.activo ? <PowerOff size={16} /> : <Power size={16} />}
                  </button>
                  <button
                    onClick={() => setDatosColapsados(!datosColapsados)}
                    title={datosColapsados ? 'Mostrar datos de la compañía' : 'Minimizar datos de la compañía'}
                    className="text-slate-400 hover:text-primary-600 transition-colors"
                  >
                    {datosColapsados ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </button>
                </div>
              </div>

              {!datosColapsados && (
                <div className="p-4 border-b grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">RIF</p>
                    <p className="text-slate-700">{clienteSeleccionado.rif || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Teléfono</p>
                    <p className="text-slate-700">{clienteSeleccionado.telefono || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Email</p>
                    <p className="text-slate-700">{clienteSeleccionado.email || '-'}</p>
                  </div>
                </div>
              )}

              <EstadoCuenta
                parte="cliente"
                entidad={clienteSeleccionado}
                empresaNombre={empresaNombre}
                esRestaurante={esRestaurante}
                onSaldoCambiado={cargar}
                accionInicial={accionInicial}
                onAccionInicialUsada={() => setAccionInicial(null)}
              />
        </div>
        )}
      </div>
    </div>
  );

  // ---------- Render de filas ----------
  function renderFilaCliente(c, prof) {
    return (
      <React.Fragment key={`c-${c.id}`}>
        <tr
          {...filaClickeable(() => { setSeleccionadoId(c.id); setListaColapsada(true); })}
          className={`border-t hover:bg-slate-50 transition-colors cursor-pointer ${seleccionadoId === c.id ? 'bg-primary-50' : ''} ${FOCO_FILA}`}
        >
          <td
            className={`p-3 break-words ${prof > 0 ? 'text-slate-600' : 'font-medium'}`}
            style={{ paddingLeft: 12 + prof * 20 }}
          >
            {c.nombre}
            {!c.activo && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-normal align-middle">Inhabilitado</span>
            )}
          </td>
          <td className={`p-3 text-right whitespace-nowrap ${Number(c.pendiente_cobrar_usd) < -0.02 ? 'text-emerald-600' : ''}`}>
            ${formatMonto(Math.abs(Number(c.pendiente_cobrar_usd)))}{Number(c.pendiente_cobrar_usd) < -0.02 ? ' a favor' : ''}
          </td>
          <td className="p-3 text-right">
            <button
              onClick={(e) => { e.stopPropagation(); alternarGrupoPanel(c.id); }}
              title="Agrupar cliente"
              className={`transition-colors ${grupoAbiertoId === c.id ? 'text-indigo-600' : 'text-slate-400 hover:text-indigo-600'}`}
            >
              <Boxes size={15} />
            </button>
          </td>
        </tr>
        {grupoAbiertoId === c.id && (
          <tr className="border-t bg-slate-50">
            <td colSpan={3} className="p-4">
              <div className="space-y-3">
                {c.grupo_id ? (
                  <p className="text-sm text-slate-600">
                    Agrupado con <span className="font-medium">{c.grupo_nombre}</span>.{' '}
                    <button onClick={() => quitarDeGrupo(c)} className="text-red-600 hover:underline">Quitar del grupo</button>
                  </p>
                ) : (
                  <p className="text-sm text-slate-400">Este cliente no pertenece a ningún grupo todavía.</p>
                )}
                <div className="max-w-xs">
                  <label className="block text-xs text-slate-500 mb-1">{c.grupo_id ? 'Cambiar a otro grupo' : 'Agrupar con...'}</label>
                  <AutocompleteGrupo
                    grupos={grupos.filter((g) => g.id !== c.grupo_id)}
                    onSeleccionar={(g) => asignarGrupo(c, g.id)}
                    onCrear={(nombre) => crearYAsignarGrupo(c, nombre)}
                    placeholder="Buscar o crear grupo..."
                  />
                </div>
                {errorGrupo && <p className="text-red-600 text-xs">{errorGrupo}</p>}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }

  function renderGrupo(nodo, prof) {
    const abierto = gruposExpandidos.has(nodo.grupo_id);
    const subs = nodo.hijosVisibles || [];
    const partes = [`${nodo.conteo} cliente${nodo.conteo === 1 ? '' : 's'}`];
    if (subs.length) partes.push(`${subs.length} grupo${subs.length === 1 ? '' : 's'}`);
    return (
      <React.Fragment key={`g-${nodo.grupo_id}`}>
        <tr
          {...filaClickeable(() => alternarExpandirGrupo(nodo.grupo_id))}
          className={`border-t bg-slate-50/60 hover:bg-slate-100 transition-colors cursor-pointer ${FOCO_FILA}`}
        >
          <td className="p-3 font-medium" style={{ paddingLeft: 12 + prof * 20 }}>
            <div className="flex items-center gap-1.5">
              {abierto ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
              <span className="break-words">{nodo.grupo_nombre}</span>
              <span className="text-xs text-slate-400 font-normal whitespace-nowrap">({partes.join(', ')})</span>
            </div>
          </td>
          <td className="p-3 text-right whitespace-nowrap text-slate-500">${formatMonto(nodo.saldo)}</td>
          <td className="p-3">
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={(e) => alternarPadrePanel(nodo.grupo_id, e)}
                title="Meter este grupo dentro de otro"
                className={`transition-colors ${grupoPadrePanelId === nodo.grupo_id ? 'text-indigo-600' : 'text-slate-400 hover:text-indigo-600'}`}
              >
                <Boxes size={14} />
              </button>
              <button
                onClick={(e) => abrirEditGrupo({ id: nodo.grupo_id, nombre: nodo.grupo_nombre }, e)}
                title="Renombrar grupo"
                className="text-slate-400 hover:text-primary-600 transition-colors"
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={(e) => eliminarGrupo({ id: nodo.grupo_id, nombre: nodo.grupo_nombre }, e)}
                title="Eliminar grupo"
                className="text-slate-400 hover:text-red-600 transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </td>
        </tr>

        {grupoEditId === nodo.grupo_id && (
          <tr className="border-t bg-slate-50">
            <td colSpan={3} className="p-4">
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Nombre del grupo</label>
                  <input
                    autoFocus
                    value={grupoEditNombre}
                    onChange={(e) => setGrupoEditNombre(e.target.value)}
                    className="border rounded-md px-2 py-1.5 text-sm"
                  />
                </div>
                <button onClick={() => guardarEditGrupo(nodo.grupo_id)} className="bg-primary-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-primary-700">
                  Guardar
                </button>
                <button onClick={() => setGrupoEditId(null)} className="text-slate-500 text-sm hover:underline">
                  Cancelar
                </button>
              </div>
              {errorGrupoEdit && <p className="text-red-600 text-xs mt-2">{errorGrupoEdit}</p>}
            </td>
          </tr>
        )}

        {grupoPadrePanelId === nodo.grupo_id && (
          <tr className="border-t bg-slate-50">
            <td colSpan={3} className="p-4">
              <div className="space-y-3">
                {nodo.padre_id ? (
                  <p className="text-sm text-slate-600">
                    Dentro de <span className="font-medium">{nodo.padre_nombre}</span>.{' '}
                    <button onClick={() => quitarPadre(nodo.grupo_id)} className="text-red-600 hover:underline">Sacar de ese grupo</button>
                  </p>
                ) : (
                  <p className="text-sm text-slate-400">Este grupo no está dentro de ningún otro grupo todavía.</p>
                )}
                <div className="max-w-xs">
                  <label className="block text-xs text-slate-500 mb-1">{nodo.padre_id ? 'Mover a otro grupo' : 'Meter dentro de...'}</label>
                  <AutocompleteGrupo
                    grupos={candidatosPadre(nodo.grupo_id)}
                    onSeleccionar={(g) => asignarPadre(nodo.grupo_id, g.id)}
                    onCrear={(nombre) => crearYAsignarPadre(nodo.grupo_id, nombre)}
                    placeholder="Buscar o crear grupo..."
                  />
                </div>
                {errorGrupoPadre && <p className="text-red-600 text-xs">{errorGrupoPadre}</p>}
              </div>
            </td>
          </tr>
        )}

        {abierto && subs.map((sub) => renderGrupo(sub, prof + 1))}
        {abierto && nodo.miembros.map((c) => renderFilaCliente(c, prof + 1))}
      </React.Fragment>
    );
  }
}
